import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Alert,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { 
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';

/**
 * DeleteConfirmDialog - Diálogo de confirmação elegante para exclusões
 * 
 * Features:
 * - Visual consistente com ícone de aviso
 * - Destaque do item a ser excluído
 * - Lista de impactos/consequências da exclusão
 * - Alertas informativos
 * - Botões bem posicionados (Cancelar/Excluir)
 * 
 * @example
 * <DeleteConfirmDialog
 *   open={dialog.open}
 *   onClose={dialog.handleClose}
 *   onConfirm={handleDelete}
 *   title="Confirmar Exclusão de Local"
 *   itemName={place.name}
 *   message="Tem certeza que deseja excluir este local?"
 *   impacts={[
 *     'Todos os serviços vinculados serão removidos',
 *     'Profissionais perderão o vínculo com este local',
 *     'Configurações de pagamento serão perdidas'
 *   ]}
 *   severity="error"
 * />
 */
export default function DeleteConfirmDialog({ 
  open, 
  onClose, 
  onConfirm, 
  title = 'Confirmar Exclusão',
  itemName,
  itemType = 'item',
  message,
  impacts = [],
  additionalInfo,
  confirmText = 'Excluir',
  cancelText = 'Cancelar',
  severity = 'warning',
  loading = false
}) {
  console.log('[DeleteConfirmDialog] Renderizando com props:', {
    open,
    title,
    itemName,
    itemType,
    severity,
    impacts: impacts?.length,
    onConfirm: typeof onConfirm,
    onClose: typeof onClose
  });

  const severityConfig = {
    warning: {
      icon: WarningIcon,
      color: 'warning',
      alertText: 'Esta ação não pode ser desfeita.'
    },
    error: {
      icon: ErrorIcon,
      color: 'error',
      alertText: 'ATENÇÃO: Esta ação é irreversível e pode ter consequências graves.'
    },
    info: {
      icon: InfoIcon,
      color: 'info',
      alertText: 'Esta operação será executada imediatamente.'
    }
  };

  const config = severityConfig[severity] || severityConfig.warning;
  const Icon = config.icon;

  // Mensagem padrão se não fornecida
  const defaultMessage = itemName 
    ? `Tem certeza que deseja excluir ${itemType} "${itemName}"?`
    : `Tem certeza que deseja excluir este ${itemType}?`;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        elevation: 8
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1.5,
        pb: 1
      }}>
        <Icon color={config.color} fontSize="large" />
        <Typography variant="h6" component="span">
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* Mensagem principal */}
        <Typography variant="body1" sx={{ mb: 2 }}>
          {message || defaultMessage}
        </Typography>

        {/* Item name destacado se fornecido */}
        {itemName && (
          <Box sx={{ 
            p: 2, 
            bgcolor: 'action.hover', 
            borderRadius: 1,
            mb: 2,
            borderLeft: 4,
            borderColor: `${config.color}.main`
          }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {itemType.charAt(0).toUpperCase() + itemType.slice(1)} a ser excluído:
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {itemName}
            </Typography>
          </Box>
        )}

        {/* Lista de impactos */}
        {impacts.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WarningIcon fontSize="small" color="warning" />
              Impactos desta ação:
            </Typography>
            <List dense disablePadding sx={{ 
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              mt: 1
            }}>
              {impacts.map((impact, index) => (
                <Box key={index}>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Box sx={{ 
                        width: 6, 
                        height: 6, 
                        bgcolor: 'warning.main', 
                        borderRadius: '50%' 
                      }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={impact}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  {index < impacts.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </Box>
        )}

        {/* Informação adicional */}
        {additionalInfo && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {additionalInfo}
          </Alert>
        )}

        {/* Alerta de confirmação */}
        <Alert severity={severity}>
          {config.alertText}
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={() => {
            console.log('[DeleteConfirmDialog] Botão Cancelar clicado');
            onClose();
          }} 
          variant="outlined"
          disabled={loading}
        >
          {cancelText}
        </Button>
        <Button 
          onClick={() => {
            console.log('[DeleteConfirmDialog] Botão Excluir clicado');
            console.log('[DeleteConfirmDialog] Tipo de onConfirm:', typeof onConfirm);
            console.log('[DeleteConfirmDialog] onConfirm:', onConfirm);
            
            try {
              if (typeof onConfirm === 'function') {
                console.log('[DeleteConfirmDialog] Chamando onConfirm...');
                onConfirm();
                console.log('[DeleteConfirmDialog] onConfirm chamado com sucesso');
              } else {
                console.error('[DeleteConfirmDialog] onConfirm NÃO É UMA FUNÇÃO!');
              }
            } catch (error) {
              console.error('[DeleteConfirmDialog] ERRO ao chamar onConfirm:', error);
              console.error('[DeleteConfirmDialog] Error stack:', error.stack);
            }
          }}
          variant="contained" 
          color={config.color}
          disabled={loading}
          autoFocus
        >
          {loading ? 'Excluindo...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DeleteConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  itemName: PropTypes.string,
  itemType: PropTypes.string,
  message: PropTypes.string,
  impacts: PropTypes.arrayOf(PropTypes.string),
  additionalInfo: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  severity: PropTypes.oneOf(['warning', 'error', 'info']),
  loading: PropTypes.bool
};
